import React, { useState, useEffect, useRef } from 'react';
import { Upload, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function DireccionPagos() {
    const [qrPreview, setQrPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [qrActivo, setQrActivo] = useState(true);
    const [bancoActivo, setBancoActivo] = useState(true);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        banco: '',
        numero_cuenta: '',
        tipo_cuenta: '',
        nombre_destinatario: '',
        glosa: ''
    });

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/direccion/pagos/configuraciones`);
            const data = await response.json();

            if (data.configuraciones && data.configuraciones.length > 0) {
                const config = data.configuraciones.find(c => c.tipo === 'preadmision') || data.configuraciones[0];

                setFormData({
                    banco: config.banco || '',
                    numero_cuenta: config.numero_cuenta || '',
                    tipo_cuenta: config.tipo_cuenta || '',
                    nombre_destinatario: config.titular || '',
                    glosa: config.nit || ''
                });

                setQrActivo(config.qr_activo !== false);
                setBancoActivo(config.banco_activo !== false);

                if (config.qr_image_path) {
                    setQrPreview(config.qr_image_path);
                }
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage('Por favor seleccione una imagen válida');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setQrPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);

            // Guardar configuración para preadmisión (id=1)
            const configData = {
                banco: formData.banco,
                numero_cuenta: formData.numero_cuenta,
                titular: formData.nombre_destinatario,
                nit: formData.glosa,
                qr_image_path: qrPreview,
                qr_activo: qrActivo,
                banco_activo: bancoActivo
            };

            // Actualizar configuraciones (1, 2, 3, 4)
            const ids = [1, 2, 3, 4];
            let success = true;

            for (const id of ids) {
                const response = await fetch(`${API_URL}/direccion/pagos/configuraciones/${id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(configData)
                });
                if (!response.ok) success = false;
            }

            if (success) {
                setMessage('Configuración guardada correctamente. Los cambios se verán reflejados en los formularios de inscripción.');
                setTimeout(() => setMessage(''), 5000);
            } else {
                setMessage('Error al guardar algunos datos');
            }
        } catch (error) {
            console.error('Error:', error);
            setMessage('Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    // Componente Switch personalizado para la estética del proyecto
    const ToggleSwitch = ({ label, active, onChange }) => (
        <div className="flex items-center justify-between mb-4 bg-slate-50 p-3 rounded-xl border border-slate-100">
            <span className="text-sm font-bold text-slate-700 uppercase tracking-tight">{label}</span>
            <button
                onClick={onChange}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    active ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
            >
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
            </button>
        </div>
    );

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto animate-in fade-in duration-500">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Dirección Académica &gt; Pagos</h1>
                <p className="text-sm text-slate-500 mt-1">
                    Gestiona los métodos de pago disponibles para los estudiantes en las plataformas de inscripción.
                </p>
            </div>

            {message && (
                <div className={`mb-6 p-4 rounded-xl border animate-in slide-in-from-top duration-300 ${
                    message.includes('Error') 
                    ? 'bg-rose-50 border-rose-200 text-rose-700' 
                    : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                }`}>
                    <div className="flex items-center gap-2 font-bold">
                        {message.includes('Error') ? '⚠️' : '✅'} {message}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Sección 1: Cargar QR */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="p-6 border-b border-slate-50 ">
                        <h2 className="text-lg font-bold uppercase flex items-center justify-between">
                            Método 1: Código QR
                        </h2>
                    </div>
                    <div className="p-6">
                        <ToggleSwitch 
                            label={qrActivo ? "Método Habilitado" : "Método Inhabilitado"} 
                            active={qrActivo} 
                            onChange={() => setQrActivo(!qrActivo)} 
                        />
                        
                        <div className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center min-h-[300px] transition-all ${
                            qrActivo ? 'border-blue-200 bg-white shadow-inner' : 'border-slate-200 bg-slate-50 opacity-60 grayscale'
                        }`}>
                            {qrPreview ? (
                                <div className="relative group">
                                    <img src={qrPreview} alt="QR Preview" className="max-w-full max-h-[240px] object-contain rounded-lg shadow-md mb-4" />
                                    {qrActivo && (
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                                            <button onClick={() => fileInputRef.current.click()} className="p-2 bg-white rounded-full text-blue-900 shadow-xl">
                                                <Upload size={20} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-slate-400 text-center mb-6">
                                    <div className="w-48 h-48 border-2 border-slate-200 flex items-center justify-center mb-4 mx-auto bg-slate-50 rounded-2xl">
                                        <Upload size={48} className="opacity-20" />
                                    </div>
                                    <p className="text-sm font-medium">Sube el código QR oficial</p>
                                </div>
                            )}

                            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                            {qrActivo && (
                                <button
                                    onClick={() => fileInputRef.current.click()}
                                    className="flex items-center gap-2 px-8 py-2.5 bg-blue-900 text-white rounded-xl hover:bg-blue-800 transition-all font-bold text-xs uppercase tracking-widest shadow-lg shadow-blue-100"
                                >
                                    {qrPreview ? 'Cambiar Imagen' : 'Subir QR'} <Upload size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sección 2: Datos Bancarios */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="p-6 border-b border-slate-50 bg-slate-50/50">
                        <h2 className="text-lg font-bold text-slate-800 uppercase">
                            Método 2: Transferencia
                        </h2>
                    </div>
                    <div className="p-6">
                        <ToggleSwitch 
                            label={bancoActivo ? "Método Habilitado" : "Método Inhabilitado"} 
                            active={bancoActivo} 
                            onChange={() => setBancoActivo(!bancoActivo)} 
                        />

                        <div className={`space-y-4 transition-all ${bancoActivo ? 'opacity-100' : 'opacity-60 grayscale pointer-events-none'}`}>
                            {[
                                { label: 'Banco', name: 'banco', placeholder: 'Ej: Banco Unión' },
                                { label: 'Número de Cuenta', name: 'numero_cuenta', placeholder: '0000000000' },
                                { label: 'Tipo de Cuenta', name: 'tipo_cuenta', placeholder: 'Caja de Ahorro' },
                                { label: 'Nombre Titular', name: 'nombre_destinatario', placeholder: 'Nombre Completo' },
                                { label: 'Referencia / Glosa sugerida', name: 'glosa', placeholder: 'Ej: Pago Inscripción' }
                            ].map((field) => (
                                <div key={field.name}>
                                    <label className="block text-[10px] font-bold uppercase  mb-1.5 ml-1">{field.label}</label>
                                    <input
                                        type="text"
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all bg-slate-50/30 text-slate-700 text-sm font-medium"
                                        placeholder={field.placeholder}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-center pt-8 border-t">
                <button
                    onClick={handleSubmit}
                    disabled={saving}
                    className="group relative px-16 py-4 bg-blue-900 text-white rounded-2xl hover:bg-blue-800 disabled:opacity-50 transition-all font-bold uppercase text-sm shadow-xl shadow-slate-200 active:scale-95"
                >
                    <div className="flex items-center gap-3">
                        <Save size={20} className={saving ? 'animate-pulse' : 'group-hover:scale-110 transition-transform'} />
                        {saving ? 'Procesando...' : 'Guardar Configuración'}
                    </div>
                </button>
            </div>
        </div>
    );
}

DireccionPagos.displayName = 'DireccionPagos';
